import { AttackPokemonData } from '../../../types/attack';
import { MAX_STAT_EFFORT, normalizeEffortValue } from '../../../constants/effort';
import type { AttackStatInputs } from '../../../types/stat';
import type { PersonalityRate } from '../../../types/damage';
import StatsInputTableCore, { StatRowConfig, EffortKey, PersonalityKey, PersonalityStatKey, StatKey } from '../../../components/StatsInputTableCore';

interface DefencePokemonStatInputAttackModeProps {
  attackPokemon: AttackPokemonData;
  handleUpdateAttackPokemonStats: (
    id: string,
    field:
      | keyof AttackStatInputs
      | 'attack_iv'
      | 'defense_iv'
      | 'special_attack_iv'
      | 'special_defense_iv'
      | 'speed_iv',
    value: number,
  ) => void;
  handleUpdateAttackPokemonPersonality: (
    id: string,
    field:
      | 'attack_personality'
      | 'defense_personality'
      | 'special_attack_personality'
      | 'special_defense_personality'
      | 'speed_personality',
    value: PersonalityRate,
  ) => void;
  handleUpdateAttackPokemonRank: (
    id: string,
    field: 'attackRank' | 'defenseRank' | 'specialAttackRank' | 'specialDefenseRank' | 'speedRank',
    value: number,
  ) => void;
}

type EffortField = keyof Pick<
  AttackStatInputs,
  | 'hp_effort'
  | 'attack_effort'
  | 'defense_effort'
  | 'special_attack_effort'
  | 'special_defense_effort'
  | 'speed_effort'
>;
type PersonalityField = keyof Pick<
  AttackStatInputs,
  | 'attack_personality'
  | 'defense_personality'
  | 'special_attack_personality'
  | 'special_defense_personality'
  | 'speed_personality'
>;
type IvField = 'attack_iv' | 'defense_iv' | 'special_attack_iv' | 'special_defense_iv' | 'speed_iv';
type RankField = 'attackRank' | 'defenseRank' | 'specialAttackRank' | 'specialDefenseRank' | 'speedRank';

// 各行の表現を定義して map で描画すれば修正時のメンテが一箇所で済む
const STAT_ROWS: StatRowConfig[] = [
  { key: 'hp', label: 'H', showIv: false, showPersonality: false, showRank: false },
  { key: 'attack', label: 'A' },
  { key: 'defense', label: 'B' },
  { key: 'special_attack', label: 'C' },
  { key: 'special_defense', label: 'D' },
  { key: 'speed', label: 'S' },
];

function DefencePokemonStatInputAttackMode({
  attackPokemon,
  handleUpdateAttackPokemonStats,
  handleUpdateAttackPokemonPersonality,
  handleUpdateAttackPokemonRank,
}: DefencePokemonStatInputAttackModeProps) {
  const statInputs = attackPokemon.statInputs;
  if (!statInputs) {
    return null;
  }

  const ivFieldMap: Record<PersonalityStatKey, IvField> = {
    attack: 'attack_iv',
    defense: 'defense_iv',
    special_attack: 'special_attack_iv',
    special_defense: 'special_defense_iv',
    speed: 'speed_iv',
  };

  const rankFieldMap: Record<PersonalityStatKey, RankField> = {
    attack: 'attackRank',
    defense: 'defenseRank',
    special_attack: 'specialAttackRank',
    special_defense: 'specialDefenseRank',
    speed: 'speedRank',
  };

  const personalityKeyMap: Record<PersonalityKey, PersonalityField> = {
    attack_personality: 'attack_personality',
    defense_personality: 'defense_personality',
    special_attack_personality: 'special_attack_personality',
    special_defense_personality: 'special_defense_personality',
    speed_personality: 'speed_personality',
  };

  const effortValues = {
    hp_effort: statInputs.hp_effort ?? 0,
    attack_effort: statInputs.attack_effort ?? 0,
    defense_effort: statInputs.defense_effort ?? 0,
    special_attack_effort: statInputs.special_attack_effort ?? 0,
    special_defense_effort: statInputs.special_defense_effort ?? 0,
    speed_effort: statInputs.speed_effort ?? 0,
  };

  const ivValues = {
    attack: statInputs.attack_iv ?? 31,
    defense: statInputs.defense_iv ?? 31,
    special_attack: statInputs.special_attack_iv ?? 31,
    special_defense: statInputs.special_defense_iv ?? 31,
    speed: statInputs.speed_iv ?? 31,
  };

  return (
    <StatsInputTableCore
      rows={STAT_ROWS}
      effortValues={effortValues}
      onEffortChange={(statKey: EffortKey, value) =>
        handleUpdateAttackPokemonStats(attackPokemon.id, statKey as EffortField, normalizeEffortValue(value))
      }
      effortMaxValue={MAX_STAT_EFFORT}
      personalityValues={{
        attack_personality: statInputs.attack_personality,
        defense_personality: statInputs.defense_personality,
        special_attack_personality: statInputs.special_attack_personality,
        special_defense_personality: statInputs.special_defense_personality,
        speed_personality: statInputs.speed_personality,
      }}
      onPersonalityChange={(key: PersonalityKey, value: PersonalityRate) =>
        handleUpdateAttackPokemonPersonality(attackPokemon.id, personalityKeyMap[key], value)
      }
      enableNatureConstraint={false}
      ivValues={ivValues as Record<StatKey, 0 | 31>}
      onIvChange={(statKey, value) => {
        if (statKey === 'hp') return;
        handleUpdateAttackPokemonStats(attackPokemon.id, ivFieldMap[statKey], value);
      }}
      rankValues={{
        attack: attackPokemon.attackRank ?? 0,
        defense: attackPokemon.defenseRank ?? 0,
        special_attack: attackPokemon.specialAttackRank ?? 0,
        special_defense: attackPokemon.specialDefenseRank ?? 0,
        speed: attackPokemon.speedRank ?? 0,
      }}
      onRankChange={(statKey, value) => handleUpdateAttackPokemonRank(attackPokemon.id, rankFieldMap[statKey], value)}
      variant="sidebar"
      columnWidths={{ stat: 20, iv: 50, effort: 35, personality: 30, rank: 60 }}
      headerLabels={{
        stat: '能力',
        iv: '個体値',
        effort: '努力値',
        personality: '性格',
        rank: 'ランク',
      }}
    />
  );
}

export default DefencePokemonStatInputAttackMode;
